<template>
  <div>
    <UPageCTA
      variant="naked"
      orientation="horizontal"
      title="Simple freelance time-management"
      description="Yet another attempt to solve an old problem. We promise continuous support, as we use it ourselves. And we are open for improvements. But you'll need to try it to find out if it covers your exact requirements."
      class="justify-center"
      :links="links"
      :ui="{
        container: 'lg:grid-cols-2 gap-y-12 lg:items-center',
        title: 'max-lg:text-center',
        description: 'max-lg:text-center',
        links: 'max-lg:justify-center',
      }"
    >
      <UPageCard
        v-if="!loggedIn"
        variant="solid"
        class="mx-auto bg-gray-200 shadow-xl text-black border-slate-200 dark:bg-gray-900 dark:text-white dark:border-gray-800 border max-w-sm w-full p-4"
      >
        <LoginForm
          :fixed-username="verifyUsername"
          @create-user-success="onCreateUserSuccess"
          @login-success="onLoginSuccess"
        />
        <UAlert
          v-if="showVerifyEmailInfoMsg"
          variant="subtle"
          color="info"
          title="Login to verify your email"
          icon="i-mdi-information-circle"
          class="max-w-xl mt-6"
        />
      </UPageCard>
    </UPageCTA>
  </div>
</template>

<script setup lang="ts">
import type { ButtonProps } from "@nuxt/ui";
const { loggedIn, user, fetch: refreshSession } = useUserSession();
const verifiedAt = computed(() => user.value?.email_verified_at);
const route = useRoute();
const showVerifyEmailInfoMsg = ref(false);
const verifyUsername = ref<undefined | string>();
const redirectTo = ref<string>(
  typeof route.query.redirectTo === "string" &&
    route.query.redirectTo.startsWith("/")
    ? route.query.redirectTo
    : "/dashboard",
);

watch(
  () => route.query,
  (q) => {
    if (q?.info === "verify-email" && typeof q?.username === "string") {
      showVerifyEmailInfoMsg.value = true;
      verifyUsername.value = q.username;
    } else {
      showVerifyEmailInfoMsg.value = false;
      verifyUsername.value = undefined;
    }

    if (typeof q?.redirectTo === "string" && q.redirectTo.startsWith("/")) {
      redirectTo.value = q.redirectTo;
    } else {
      redirectTo.value = "/dashboard";
    }
  },
  { deep: true, immediate: true },
);

if (loggedIn.value && verifiedAt && route.query.redirectTo) {
  console.log("redirecting to", toRaw(redirectTo.value));
  await navigateTo(redirectTo.value);
} else if (loggedIn.value && !verifiedAt.value) {
  console.warn("looks like user is not logged in!", {
    loggedIn: loggedIn.value,
    verifiedAt: verifiedAt.value,
    user: toRaw(user.value),
  });
  await navigateTo("/account-needs-verification");
}

async function onLoginSuccess() {
  console.log("on login success!");
  await refreshSession();
  await navigateTo(redirectTo.value);
  showVerifyEmailInfoMsg.value = false;
  verifyUsername.value = undefined;
}

async function onCreateUserSuccess() {
  console.log("on create user success!");
  // do nothing
}

const links = computed<ButtonProps[]>(() => {
  const whitelabel = {
    label: "I want a whitelabel version",
    color: "secondary" as const,
    trailingIcon: "i-lucide-arrow-right",
    class: "cursor-pointer",
    to: "/get-in-touch",
  };
  const createacc = {
    label: "Create free account",
    color: "success" as const,
    trailingIcon: "i-lucide-arrow-right",
    class: "cursor-pointer",
    to: "/create-account",
  };

  if (loggedIn.value) {
    return [whitelabel];
  } else {
    return [createacc, whitelabel];
  }
});
</script>
